'use client';

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Stack,
  Card,
  CardContent,
  Fab,
  Zoom,
  alpha,
  useMediaQuery,
  useTheme,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { formatLocationLabel } from '../../utils/locationUtils';
import {
  Refresh as RefreshIcon,
  LocalShipping,
  Add,
  ViewModule,
  ViewList,
  Visibility,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { SOCKET_EVENTS } from '@/hooks/socket';
import { PARCEL_STATUS, PARCEL_FILTERS } from '../../constants/parcelEnums';
import { LayoutContainer, SmartFilter, CompactParcelCard, ModernPagination } from '../../kit';
import UnifiedBookingDetailsSheet from '../../components/common/UnifiedBookingDetailsSheet';
import { themeColors } from '../../theme/theme';
import { ParcelsGridShimmer } from '../../components/common/LoadingShimmer';
import { useRouter } from 'next/navigation';
import { useActiveParcelsQuery, useInvalidateParcels } from '@/hooks/useParcelsQuery';
import BulkBadge from '@/components/BulkBadge';
import { useTranslation } from '../../kit/useTranslation';

export default function ActiveParcelsPage() {
  const router = useRouter();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation();

  const [selectedParcel, setSelectedParcel] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(PARCEL_STATUS.ALL);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState('table');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const itemsPerPage = isDesktop ? 12 : 8;

  const filterOptions = useMemo(
    () => [
      ...Object.values(PARCEL_STATUS),
      { value: PARCEL_FILTERS.MULTI_STAGE, label: t('activeParcels.multiStage') },
    ],
    []
  );

  // Use React Query hook - shares cache with other pages
  const { data: parcels = [], isLoading: loading, error, refetch } = useActiveParcelsQuery();
  const { invalidateAll } = useInvalidateParcels();

  // Socket is initialized globally in AppProviders - just listen to custom window events
  // No need to create another socket instance here

  const handleParcelStatusUpdate = useCallback(
    (event) => {
      const data = event.detail;
      if (!data || !data.parcelId) return;
      // Invalidate cache to trigger refetch
      invalidateAll();
    },
    [invalidateAll]
  );

  // Listen for parcel status updates via socket events - removed useWindowEvent calls
  // Socket events are now handled in the app-level socket provider

  const filteredParcels = useMemo(() => {
    let filtered = parcels;
    if (statusFilter === PARCEL_FILTERS.MULTI_STAGE) {
      filtered = filtered.filter(
        (p) => p.parcelIsMultiStage || (p.parcelTotalLegs && p.parcelTotalLegs > 1)
      );
    } else if (statusFilter !== PARCEL_STATUS.ALL) {
      filtered = filtered.filter((p) => p.parcelStatus === statusFilter);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.parcelId?.toString().includes(query) ||
          p.parcelPickupAddress?.label?.toLowerCase().includes(query) ||
          p.parcelDeliveryAddress?.label?.toLowerCase().includes(query) ||
          p.parcelRiderName?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'id':
          aVal = a.parcelId || 0;
          bVal = b.parcelId || 0;
          break;
        case 'price':
          aVal = a.parcelPrice || a.parcelAmount || 0;
          bVal = b.parcelPrice || b.parcelAmount || 0;
          break;
        case 'status':
          aVal = a.parcelStatus || '';
          bVal = b.parcelStatus || '';
          return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        case 'date':
        default:
          aVal = new Date(a.parcelCreatedAt || a.parcelUpdatedAt || 0);
          bVal = new Date(b.parcelCreatedAt || b.parcelUpdatedAt || 0);
          break;
      }

      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return sorted;
  }, [parcels, statusFilter, searchQuery, sortBy, sortOrder]);

  const paginatedParcels = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return filteredParcels.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredParcels, page, itemsPerPage]);

  const totalPages = Math.ceil(filteredParcels.length / itemsPerPage);

  // Smart insights
  const insights = useMemo(() => {
    const inTransit = parcels.filter((p) => p.parcelStatus === 'IN_TRANSIT').length;
    const pickedUp = parcels.filter((p) => p.parcelStatus === 'PICKED_UP').length;
    const outForDelivery = parcels.filter((p) => p.parcelStatus === 'OUT_FOR_DELIVERY').length;
    const multiStage = parcels.filter((p) => p.parcelIsMultiStage || p.parcelTotalLegs > 1).length;
    const avgDeliveryTime = '2.5 hrs';
    return { inTransit, pickedUp, outForDelivery, multiStage, avgDeliveryTime };
  }, [parcels]);

  const openParcelDetails = (parcel) => setSelectedParcel(parcel);
  const closeParcelDetails = () => setSelectedParcel(null);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const SortableHeader = ({ column, label, align = 'left' }) => (
    <TableCell
      align={align}
      onClick={() => handleSort(column)}
      sx={{
        fontWeight: 700,
        color: '#374151',
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { bgcolor: '#f3f4f6' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          justifyContent: align === 'center' ? 'center' : 'flex-start',
        }}
      >
        {label}
        {sortBy === column &&
          (sortOrder === 'asc' ? (
            <ArrowUpward sx={{ fontSize: 16 }} />
          ) : (
            <ArrowDownward sx={{ fontSize: 16 }} />
          ))}
      </Box>
    </TableCell>
  );

  return (
    <LayoutContainer>
      <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', pb: 10 }}>
        {/* Page Title Section - Clean & Minimal */}
        <Box
          sx={{
            bgcolor: '#fff',
            borderBottom: '1px solid #e5e7eb',
            py: 2,
            px: { xs: 2, md: 3 },
          }}
        >
          <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    fontWeight: 700,
                    color: '#1f2937',
                  }}
                >
                  {t('activeParcels.title')}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>
                  {t('activeParcels.activeDeliveries', { count: filteredParcels.length })}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {insights.inTransit > 0 && (
                  <Chip
                    label={t('activeParcels.inTransit', { count: insights.inTransit })}
                    size="small"
                    sx={{
                      bgcolor: '#e0e7ff',
                      color: '#4338ca',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      display: { xs: 'none', sm: 'flex' },
                    }}
                  />
                )}
                {/* View Toggle - Desktop only */}
                {isDesktop && (
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(e, newView) => newView && setViewMode(newView)}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        border: '1px solid #e5e7eb',
                        px: 1.5,
                        py: 0.5,
                        '&.Mui-selected': {
                          bgcolor: themeColors.primaryColor,
                          color: '#fff',
                          '&:hover': {
                            bgcolor: alpha(themeColors.primaryColor, 0.85),
                          },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="cards" aria-label="card view">
                      <ViewModule fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="table" aria-label="table view">
                      <ViewList fontSize="small" />
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
                <IconButton
                  onClick={() => refetch()}
                  disabled={loading}
                  size="small"
                  sx={{
                    bgcolor: '#f3f4f6',
                    '&:hover': { bgcolor: '#e5e7eb' },
                  }}
                >
                  <RefreshIcon
                    sx={{
                      fontSize: 18,
                      animation: loading ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, mt: 2 }}>
          {/* Smart Filter */}
          <SmartFilter
            options={filterOptions}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              setPage(1);
            }}
            onSearch={(query) => {
              setSearchQuery(query);
              setPage(1);
            }}
            searchPlaceholder={t('activeParcels.searchPlaceholder')}
            quickFilterCount={4}
          />

          {/* Loading */}
          {loading && <ParcelsGridShimmer count={6} />}

          {/* Error */}
          {error && !loading && (
            <Card sx={{ bgcolor: '#fef2f2', border: '1px solid #fecaca', mb: 2 }}>
              <CardContent>
                <Typography sx={{ color: '#b91c1c', fontSize: '0.9rem' }}>{error}</Typography>
              </CardContent>
            </Card>
          )}

          {/* Empty State */}
          {!loading && filteredParcels.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <LocalShipping sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
              <Typography
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: '#374151',
                  mb: 1,
                }}
              >
                {t('activeParcels.noActiveParcels')}
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', color: '#6b7280', mb: 3 }}>
                {searchQuery || statusFilter !== PARCEL_STATUS.ALL
                  ? t('activeParcels.adjustFilters')
                  : t('activeParcels.startBooking')}
              </Typography>
            </Box>
          )}

          {/* Parcels Grid/Table */}
          {!loading && paginatedParcels.length > 0 && (
            <>
              {/* Table View - Desktop only */}
              {viewMode === 'table' && isDesktop ? (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ border: '1px solid #e5e7eb', borderRadius: 2 }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f9fafb' }}>
                        <SortableHeader column="id" label={t('activeParcels.id')} />
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                          {t('activeParcels.from')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                          {t('activeParcels.to')}
                        </TableCell>
                        <SortableHeader column="status" label={t('activeParcels.status')} />
                        <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                          {t('activeParcels.rider')}
                        </TableCell>
                        <SortableHeader column="price" label={t('activeParcels.price')} />
                        <SortableHeader column="date" label={t('activeParcels.date')} />
                        <TableCell align="center" sx={{ fontWeight: 700, color: '#374151' }}>
                          {t('activeParcels.action')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedParcels.map((parcel) => {
                        const statusColors = {
                          IN_TRANSIT: { bg: '#dbeafe', color: '#1d4ed8' },
                          PICKED_UP: { bg: '#fef3c7', color: '#d97706' },
                          ASSIGNED: { bg: '#e0e7ff', color: '#4f46e5' },
                          OUT_FOR_DELIVERY: { bg: '#dcfce7', color: '#15803d' },
                        };
                        const statusStyle = statusColors[parcel.parcelStatus] || {
                          bg: '#f3f4f6',
                          color: '#6b7280',
                        };
                        const isBulk = parcel.bulkOrderId || parcel.bulk_order_id;

                        return (
                          <TableRow
                            key={parcel.parcelId}
                            hover
                            onClick={() => openParcelDetails(parcel)}
                            sx={{
                              cursor: 'pointer',
                              bgcolor: isBulk ? 'rgba(17, 24, 39, 0.04)' : 'transparent',
                              border: isBulk ? '1px solid #111827' : 'none',
                              '&:hover': {
                                bgcolor: isBulk ? 'rgba(17, 24, 39, 0.08)' : '#f9fafb',
                              },
                            }}
                          >
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: themeColors.primaryColor,
                              }}
                            >
                              #{parcel.parcelId}
                            </TableCell>
                            <TableCell
                              sx={{
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatLocationLabel(parcel.parcelPickupAddress) || '-'}
                            </TableCell>
                            <TableCell
                              sx={{
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatLocationLabel(parcel.parcelDeliveryAddress) || '-'}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Chip
                                  label={
                                    parcel.parcelStatus?.replace(/_/g, ' ') ||
                                    t('activeParcels.unknown')
                                  }
                                  size="small"
                                  sx={{
                                    bgcolor: statusStyle.bg,
                                    color: statusStyle.color,
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                  }}
                                />
                                {parcel.bulkOrderId && (
                                  <BulkBadge
                                    bulkOrderId={parcel.bulkOrderId}
                                    bulkOrderReference={parcel.bulkOrderReference}
                                    itemCount={parcel.bulkItemCount}
                                  />
                                )}
                                {(parcel.created_via_admin_console || parcel.createdViaAdminConsole) && (
                                  <Chip
                                    label="Admin"
                                    size="small"
                                    sx={{
                                      bgcolor: '#fef3c7',
                                      color: '#b45309',
                                      fontWeight: 600,
                                      fontSize: '0.65rem',
                                      height: 20,
                                    }}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ color: '#6b7280', fontSize: '0.85rem' }}>
                              {parcel.parcelRiderName || parcel.parcelRiderId || '-'}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              ₹{parcel.parcelPrice || parcel.parcelAmount || 0}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.85rem', color: '#6b7280' }}>
                              {parcel.parcelCreatedAt
                                ? new Date(parcel.parcelCreatedAt).toLocaleDateString('en-IN', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                  })
                                : '-'}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openParcelDetails(parcel);
                                }}
                                sx={{ color: themeColors.primaryColor }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                /* Cards View */
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'repeat(2, 1fr)',
                      lg: 'repeat(3, 1fr)',
                    },
                    gap: 2,
                  }}
                >
                  {paginatedParcels.map((parcel) => (
                    <CompactParcelCard
                      key={parcel.parcelId}
                      booking={parcel}
                      onClick={() => openParcelDetails(parcel)}
                    />
                  ))}
                </Box>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <ModernPagination
                    page={page}
                    totalPages={totalPages}
                    onChange={(e, value) => setPage(value)}
                    size="small"
                  />
                </Box>
              )}
            </>
          )}
        </Box>

        {/* FAB - Quick New Booking */}
        <Zoom in={!loading}>
          <Fab
            color="primary"
            onClick={() => router.push('/booking')}
            sx={{
              position: 'fixed',
              bottom: 80,
              right: 20,
              bgcolor: themeColors.primaryColor,
              '&:hover': { bgcolor: '#4f46e5' },
            }}
          >
            <Add />
          </Fab>
        </Zoom>
      </Box>

      {selectedParcel && (
        <UnifiedBookingDetailsSheet
          booking={selectedParcel}
          onClose={closeParcelDetails}
          onRefresh={() => refetch()}
        />
      )}
    </LayoutContainer>
  );
}
